import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { DropDown, DropDownItem, ICONS } from 'components/DropDown'
import { Popup } from 'components/Popup'
import Toast from 'components/Toast'
import { getMessage } from 'lib/translator'
import { buildPickingLogLink } from 'containers/App/fulfilmentPickingConfig'
import AssignPickerForm from '../../OrderActions/AssignPicker'
import PrintOrderForm from '../../OrderActions/PrintOrder'
import {
  buildAssignPickerData,
  buildPrintOrderData,
  canAssignPicker,
  canEditPacked,
  canPrintLabels,
  getPackageCount,
} from './eligibility'

/**
 * DSP page-header actions: View logs + overflow (Assign / Edit Packed / Print).
 * View logs opens the DSP Picking Log (LIT-373). Overflow matches LIT-415.
 */
const DspOrderActions = ({
  order,
  packlistData = [],
  onReload,
  isHalalCheck = false,
  isEditingPacked = false,
  onStartEditPacked,
  usePickingDeliveryOrdersPaths = false,
}) => {
  const [assignPopupOpen, setAssignPopupOpen] = useState(false)
  const [printPopupOpen, setPrintPopupOpen] = useState(false)
  const [showAssignToast, setShowAssignToast] = useState(false)

  const assignEnabled = canAssignPicker({ order, packlistData })
  const editEnabled = canEditPacked({ order, packlistData })
  const printEnabled = canPrintLabels({ order, packlistData })

  const showOverflow = assignEnabled || printEnabled || editEnabled
  const packageCount = getPackageCount(packlistData)
  const referenceNumber = order?.referenceNumber
  const orderId = order?.orderId
  const pickingLogPath = buildPickingLogLink({
    orderId,
    referenceNumber,
    usePickingDeliveryOrdersPaths,
  })

  const closeAssignPopup = () => setAssignPopupOpen(false)
  const closePrintPopup = () => setPrintPopupOpen(false)

  const handleAssignComplete = () => {
    closeAssignPopup()
    setShowAssignToast(true)
    onReload?.()
  }

  const printLabel = packageCount
    ? getMessage('picking.orderDetail.dsp.actions.printLabels.withCount', {
        count: packageCount,
      })
    : getMessage('picking.orderDetail.dsp.actions.printLabels')

  return (
    <div
      className="dsp-order-detail__action-group"
      data-testid="dsp-order-actions-header"
    >
      {isEditingPacked ? (
        <span
          className="dsp-order-detail__editing-badge"
          data-testid="dsp-editing-badge"
        >
          {getMessage('picking.orderDetail.dsp.actions.editPacked.editing')}
        </span>
      ) : null}

      {pickingLogPath ? (
        <button
          type="button"
          className="invoice-button"
          data-testid="dsp-action-view-logs"
        >
          <Link to={pickingLogPath}>{getMessage('order.actions.logs')}</Link>
        </button>
      ) : null}

      {showOverflow ? (
        <DropDown
          className="order-actions-dropdown dsp-order-detail__actions-dropdown"
          icon={
            <img
              src={ICONS.VELLIP}
              alt="⋮"
              data-testid="dsp-order-actions-overflow"
            />
          }
        >
          {assignEnabled ? (
            <DropDownItem
              onClick={() => setAssignPopupOpen(true)}
              dataTestid="dsp-action-assign-picker"
            >
              {getMessage('picking.orderDetail.dsp.actions.assignPicker')}
            </DropDownItem>
          ) : null}
          {printEnabled ? (
            <DropDownItem
              onClick={() => setPrintPopupOpen(true)}
              dataTestid="dsp-action-print-labels"
            >
              {printLabel}
            </DropDownItem>
          ) : null}
          {editEnabled ? (
            <DropDownItem
              onClick={() => onStartEditPacked?.()}
              dataTestid="dsp-action-edit-packed"
            >
              {getMessage('picking.orderDetail.dsp.actions.editPacked')}
            </DropDownItem>
          ) : null}
        </DropDown>
      ) : null}

      {assignPopupOpen ? (
        <Popup
          heading={getMessage('deliveryOrder.details.action.assignPicker')}
          show={assignPopupOpen}
          close={closeAssignPopup}
          className="assign-picker-modal"
        >
          <AssignPickerForm
            data={buildAssignPickerData(order)}
            orderReferenceNumber={referenceNumber}
            onCancel={closeAssignPopup}
            updateCompleted={handleAssignComplete}
            packlistData={packlistData}
          />
        </Popup>
      ) : null}

      {printPopupOpen ? (
        <Popup
          heading={getMessage(
            'picking.orderDetail.dsp.actions.printLabels.popupHeading',
            { count: packageCount }
          )}
          show={printPopupOpen}
          close={closePrintPopup}
          className="print-tote-label-modal"
        >
          <PrintOrderForm
            data={buildPrintOrderData(order)}
            packlistData={packlistData}
            orderReferenceNumber={referenceNumber}
            childOrderId={order?.childOrderId}
            poaChildOrderId={order?.poaChildOrderId}
            isHalalCheck={isHalalCheck}
            onCancel={closePrintPopup}
          />
        </Popup>
      ) : null}

      {showAssignToast ? (
        <Toast
          content={getMessage(
            'picking.orderDetail.dsp.actions.assignPicker.success'
          )}
          cancelMessage={getMessage('dialog.okText')}
          timer={4000}
          toastColor="#333333"
          onClose={() => setShowAssignToast(false)}
        />
      ) : null}
    </div>
  )
}

export default DspOrderActions
